/*
 * Settings screen (user-level): password management + basic profile readout
 * Props:
 *  - user   : current user object (expects .email, .first_name, .last_name, .uses_custom_workflow)
 *  - jwt    : current auth token
 *  - apiBase: backend base URL (same one App.jsx uses for other calls)
 *  - onOpenTerminologySettings: optional callback to open full TerminologySettings view
 *  - onOpenWorkflowBuilder   : optional callback to open full WorkflowBuilder view
 */

import React from "react";
import { useAuth } from "./AuthContext";

// --- Password strength helpers (mirror ResetPassword.jsx) ---
function passwordIssues(pw = "", email = "") {
  const issues = [];
  const local = (email || "").split("@")[0] || "";
  if (pw.length < 12) issues.push("At least 12 characters");
  if (!/[a-z]/.test(pw)) issues.push("At least one lowercase letter");
  if (!/[A-Z]/.test(pw)) issues.push("At least one uppercase letter");
  if (!/[0-9]/.test(pw)) issues.push("At least one number");
  if (!/[^A-Za-z0-9\s]/.test(pw)) issues.push("At least one symbol");
  if (/\s/.test(pw)) issues.push("No spaces");
  if (local && pw.toLowerCase().includes(local.toLowerCase())) {
    issues.push("Must not contain your email username");
  }
  return issues;
}
const isStrongPassword = (pw, email) => passwordIssues(pw, email).length === 0;

export default function Settings({
  user,
  jwt,
  apiBase,
  onOpenTerminologySettings,
  onOpenWorkflowBuilder,
}) {

  const { setUserAndTick } = useAuth();
  // Existing state for "send reset link" fallback
  const [msg, setMsg] = React.useState("");
  const [pending, setPending] = React.useState(false);

  // New state for Change Password form
  const [currentPassword, setCurrentPassword] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [changePending, setChangePending] = React.useState(false);
  const [changeError, setChangeError] = React.useState("");
  const [changeMsg, setChangeMsg] = React.useState("");

  // Show/hide toggles (reusing logic from ResetPassword.jsx)
  const [showCurrentPassword, setShowCurrentPassword] = React.useState(false);
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  const sendSelfReset = async () => {
    try {
      setMsg("");
      setPending(true);
      const r = await fetch(`${apiBase}/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: user?.email || "" }),
        credentials: "include",
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.detail || `Request failed (${r.status})`);
      setMsg("A reset link was sent to your e-mail.");
    } catch (e) {
      setMsg(String(e.message || e));
    } finally {
      setPending(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setChangeError("");
    setChangeMsg("");

    if (!currentPassword || !newPassword || !confirmPassword) {
      setChangeError("Please fill in all fields.");
      return;
    }

    const issues = passwordIssues(newPassword, user?.email || "");
    if (issues.length) {
      setChangeError(
        "Please choose a stronger password:\n• " + issues.join("\n• ")
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      setChangeError("Passwords do not match.");
      return;
    }

    if (!jwt) {
      setChangeError("You must be logged in to change your password.");
      return;
    }

    setChangePending(true);
    try {
      const r = await fetch(`${apiBase}/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
        credentials: "include",
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        throw new Error(data?.detail || `Request failed (${r.status})`);
      }

      setChangeMsg("Your password has been updated.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (e) {
      setChangeError(String(e.message || e));
    } finally {
      setChangePending(false);
    }
  };

  const handleOpenWorkflowBuilder = () => {
    if (onOpenWorkflowBuilder) {
      onOpenWorkflowBuilder();
    } else {
      // Fallback: old behavior, direct navigation
      window.location.href = "/workflow-builder";
    }
  };

  const handleOpenTerminology = () => {
    if (onOpenTerminologySettings) {
      onOpenTerminologySettings();
    }
  };

  // Workflow Mode Toggle - Generic and Custom
  const [modeMsg, setModeMsg] = React.useState("");
  const [modeErr, setModeErr] = React.useState("");
  const [modePending, setModePending] = React.useState(false);
  // Local mirror so we can reflect the toggle without a full reload
  const [localUsesCustom, setLocalUsesCustom] = React.useState(!!(user?.uses_custom_workflow));

  const handleSwitchToGeneric = async () => {
    try {
      setModeMsg("");
      setModeErr("");
      setModePending(true);
      const r = await fetch(`${apiBase}/workflow/mode`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ uses_custom_workflow: false }),
        credentials: "include",
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.detail || `Request failed (${r.status})`);
      setModeMsg("Switched to Generic Workflow.");
      setLocalUsesCustom(false);

      // NEW: refresh /me so the app sees uses_custom_workflow immediately
      try {
        const me = await fetch(`${apiBase}/me`, {
          headers: { Authorization: `Bearer ${jwt}` },
          credentials: "include",
        });
        if (me.ok) {
          const meData = await me.json();
          setUserAndTick && setUserAndTick(meData);
        }
      } catch (_) {}
    } catch (e) {
      setModeErr(String(e.message || e));
    } finally {
      setModePending(false);
    }
  };

  const handleSwitchToCustom = async () => {
    try {
      setModeMsg("");
      setModeErr("");
      setModePending(true);
      const r = await fetch(`${apiBase}/workflow/mode`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ uses_custom_workflow: true }),
        credentials: "include",
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.detail || `Request failed (${r.status})`);
      setModeMsg("Switched to Custom Workflow.");
      setLocalUsesCustom(true);

      // Refresh /me so the app sees uses_custom_workflow immediately
      try {
        const me = await fetch(`${apiBase}/me`, {
          headers: { Authorization: `Bearer ${jwt}` },
          credentials: "include",
        });
        if (me.ok) {
          const meData = await me.json();
          setUserAndTick && setUserAndTick(meData);
        }
      } catch (_) {}
      return true;
    } catch (e) {
      setModeErr(String(e.message || e));
      return false;
    } finally {
      setModePending(false);
    }
  };


  const modeOptionClass = (isActive) =>
    `px-3 py-1 rounded-full text-sm font-semibold border transition ${
      isActive ? "text-white shadow-sm" : "bg-white hover:opacity-90"
    }`;

  const modeOptionStyle = (isActive) =>
    isActive
      ? { backgroundColor: "#00a7e1", borderColor: "#00a7e1" }
      : { color: "#00a7e1", borderColor: "#00a7e1" };

  const modeTrackStyle = {
    backgroundColor: "#e5e7eb",
    width: "34px",
    minWidth: "34px",
    height: "18px",
    borderRadius: "9999px",
    position: "relative",
    transition: "background-color 0.2s ease",
  };

  const modeKnobStyle = {
    position: "absolute",
    top: "2px",
    left: localUsesCustom ? "2px" : "18px",
    width: "14px",
    height: "14px",
    borderRadius: "9999px",
    backgroundColor: "#00a7e1",
    transition: "left 0.2s ease",
  };

  // Terminology button visibility based on workflow draft
  const [terminologyEnabledInDraft, setTerminologyEnabledInDraft] = React.useState(true);
  React.useEffect(() => {
    async function fetchDraft() {
      try {
        const r = await fetch(`${apiBase}/workflow/draft/me`, {
          headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
          credentials: "include",
        });
        if (!r.ok) return;
        const data = await r.json().catch(() => ({}));
        setTerminologyEnabledInDraft(!!data?.terminology_enabled);
      } catch (_) {}
    }
    if (jwt) fetchDraft();
  }, [apiBase, jwt]);


  return (
    <div
      className="max-w-xl mx-auto bg-white rounded-lg shadow p-6 space-y-4"
      data-testid="settings-page"
    >
      <h2 className="text-xl font-semibold mb-2" data-testid="settings-title">
        Account Settings
      </h2>

      <div className="text-sm" data-testid="settings-account-summary">
        <div className="mb-1" data-testid="settings-account-name">
          <b>Name:</b> {(user?.first_name || "")} {(user?.last_name || "")}
        </div>
        <div className="mb-1" data-testid="settings-account-email">
          <b>E-mail Address:</b> {user?.email}
        </div>
      </div>

      {/* Workflow Mode section */}
      <div className="border-t pt-4" data-testid="settings-workflow-section">
        <h3 className="font-semibold mb-2" data-testid="settings-workflow-heading">
          Workflow
        </h3>
        <div className="text-sm text-gray-800 mb-3 flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center gap-2">
            <button
              type="button"
              onClick={async () => {
                if (modePending || localUsesCustom) return;
                const ok = await handleSwitchToCustom();
                if (ok) {
                  handleOpenWorkflowBuilder();
                }
              }}
              disabled={modePending || localUsesCustom}
              className={`${modeOptionClass(localUsesCustom)} ${
                modePending || localUsesCustom ? "cursor-default" : ""
              }`}
              style={modeOptionStyle(localUsesCustom)}
              aria-pressed={localUsesCustom}
              data-testid="settings-workflow-custom"
            >
              Custom Workflow
            </button>

            <button
              type="button"
              onClick={async () => {
                if (modePending) return;
                if (localUsesCustom) {
                  await handleSwitchToGeneric();
                } else {
                  const ok = await handleSwitchToCustom();
                  if (ok) {
                    handleOpenWorkflowBuilder();
                  }
                }
              }}
              disabled={modePending}
              className={`inline-flex items-center justify-center border ${
                modePending ? "cursor-default opacity-70" : "cursor-pointer"
              }`}
              style={modeTrackStyle}
              aria-label={`Switch to ${localUsesCustom ? "Generic Workflow" : "Custom Workflow"}`}
              title={`Switch to ${localUsesCustom ? "Generic Workflow" : "Custom Workflow"}`}
              data-testid="settings-workflow-toggle"
            >
              <span style={modeKnobStyle} />
            </button>

            <button
              type="button"
              onClick={handleSwitchToGeneric}
              disabled={modePending || !localUsesCustom}
              className={`${modeOptionClass(!localUsesCustom)} ${
                modePending || !localUsesCustom ? "cursor-default" : ""
              }`}
              style={modeOptionStyle(!localUsesCustom)}
              aria-pressed={!localUsesCustom}
              data-testid="settings-workflow-generic"
            >
              Generic Workflow
            </button>
          </div>
        </div>

        {!localUsesCustom && (
          <p
            className="text-sm text-gray-700 mb-3"
            data-testid="settings-workflow-generic-message"
          >
            You are currently using the <b>Generic Workflow</b>. Click <b>Custom Workflow</b> above to configure custom settings.
          </p>
        )}

        {localUsesCustom && (
          <div className="mb-3">
            <button
              type="button"
              onClick={handleOpenWorkflowBuilder}
              className="text-sm text-white px-4 py-2 rounded hover:opacity-90"
              style={{ backgroundColor: "#94c83d" }}
              data-testid="settings-workflow-edit-custom"
            >
              Edit Custom Workflow
            </button>
          </div>
        )}

        {(modeMsg || modeErr) && (
          <div className="mt-2 text-xs">
            {modeMsg && (
              <div
                style={{ color: "#00a7e1" }}
                data-testid="settings-workflow-success"
              >
                {modeMsg}
              </div>
            )}
            {modeErr && (
              <div className="text-red-600" data-testid="settings-workflow-error">
                {modeErr}
              </div>
            )}
          </div>
        )}
      </div>
{/* Change Password section */}
      <div className="border-t pt-4" data-testid="settings-password-section">
        <h3 className="font-semibold mb-2" data-testid="settings-password-heading">
          Change Password
        </h3>
        <p className="text-sm text-gray-700 mb-2">
          Update your password. You&apos;ll need your current password to change it.
        </p>

        <form onSubmit={handleChangePassword} className="space-y-3 text-sm">
          {/* Current password */}
          <div className="flex items-center gap-2">
            <label className="w-36 text-right" htmlFor="current-password">
              Current:
            </label>
            <div className="flex flex-1 items-center gap-2">
              <input
                id="current-password"
                data-testid="settings-current-password"
                type={showCurrentPassword ? "text" : "password"}
                className="p-2 border rounded flex-1"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Current password"
                required
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword((v) => !v)}
                className="px-2 py-1 border rounded flex items-center justify-center"
                aria-label={
                  showCurrentPassword ? "Hide current password" : "Show current password"
                }
                data-testid="settings-current-password-toggle"
              >
                <img
                  src={showCurrentPassword ? "/show_password.png" : "/hide_password.png"}
                  alt={
                    showCurrentPassword ? "Hide password" : "Show password"
                  }
                  className="h-4 w-4"
                />
              </button>
            </div>
          </div>

          {/* New password */}
          <div className="flex items-center gap-2">
            <label className="w-36 text-right" htmlFor="new-password">
              New:
            </label>
            <div className="flex flex-1 items-center gap-2">
              <input
                id="new-password"
                data-testid="settings-new-password"
                type={showNewPassword ? "text" : "password"}
                className="p-2 border rounded flex-1"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="New password"
                minLength={12}
                required
              />
              <button
                type="button"
                onClick={() => setShowNewPassword((v) => !v)}
                className="px-2 py-1 border rounded flex items-center justify-center"
                aria-label={showNewPassword ? "Hide new password" : "Show new password"}
                data-testid="settings-new-password-toggle"
              >
                <img
                  src={showNewPassword ? "/show_password.png" : "/hide_password.png"}
                  alt={showNewPassword ? "Hide password" : "Show password"}
                  className="h-4 w-4"
                />
              </button>
            </div>
          </div>

          {/* Confirm password */}
          <div className="flex items-center gap-2">
            <label className="w-36 text-right" htmlFor="confirm-password">
              Confirm:
            </label>
            <div className="flex flex-1 items-center gap-2">
              <input
                id="confirm-password"
                data-testid="settings-confirm-password"
                type={showConfirmPassword ? "text" : "password"}
                className="p-2 border rounded flex-1"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                minLength={12}
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((v) => !v)}
                className="px-2 py-1 border rounded flex items-center justify-center"
                aria-label={
                  showConfirmPassword
                    ? "Hide confirm password"
                    : "Show confirm password"
                }
                data-testid="settings-confirm-password-toggle"
              >
                <img
                  src={
                    showConfirmPassword ? "/show_password.png" : "/hide_password.png"
                  }
                  alt={
                    showConfirmPassword ? "Hide password" : "Show password"
                  }
                  className="h-4 w-4"
                />
              </button>
            </div>
          </div>

          {/* Live password strength guidance */}
          {newPassword && (
            <div className="mt-1 text-xs" data-testid="settings-password-strength">
              {isStrongPassword(newPassword, user?.email || "") ? (
                <div
                  style={{ color: "#94c83d" }}
                  data-testid="settings-password-strength-strong"
                >
                  Strong password ✅
                </div>
              ) : (
                <ul
                  className="list-disc ml-5 text-red-700"
                  data-testid="settings-password-strength-issues"
                >
                  {passwordIssues(newPassword, user?.email || "").map((msg) => (
                    <li
                      key={msg}
                      data-testid="settings-password-strength-issue"
                    >
                      {msg}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Live mismatch message */}
          {newPassword &&
            confirmPassword &&
            newPassword !== confirmPassword && (
              <div
                className="mt-1 text-xs text-red-700"
                aria-live="polite"
                data-testid="settings-password-mismatch-live"
              >
                Passwords do not match.
              </div>
            )}

          <button
            type="submit"
            disabled={changePending}
            className={`text-white px-4 py-2 rounded ${
              changePending ? "opacity-50 cursor-not-allowed" : "hover:opacity-90"
            }`}
            style={{ backgroundColor: "#00a7e1" }}
            data-testid="settings-password-submit"
          >
            {changePending ? "Updating…" : "Update Password"}
          </button>

          {changeError && (
            <div
              className="mt-2 text-xs text-red-600 whitespace-pre-line"
              data-testid="settings-password-error"
            >
              {changeError}
            </div>
          )}
          {changeMsg && !changeError && (
            <div
              className="mt-2 text-xs"
              style={{ color: "#00a7e1" }}
              data-testid="settings-password-success"
            >
              {changeMsg}
            </div>
          )}
        </form>

        {/* Optional fallback link to send reset e-mail */}
        <div className="mt-3 text-xs text-gray-600">
          Forgot your current password?{" "}
          <button
            type="button"
            onClick={sendSelfReset}
            disabled={pending}
            className="underline"
            style={{ color: "#00a7e1" }}
            data-testid="settings-password-reset-link"
          >
            {pending ? "Sending reset link..." : "Send a reset link to your e-mail."}
          </button>
        </div>
        {msg && (
          <div
            className="mt-1 text-xs"
            style={{ color: "#00a7e1" }}
            data-testid="settings-password-reset-message"
          >
            {msg}
          </div>
        )}
      </div>

      
      {/* (Data Usage / TMX section moved to CustomWorkflow.jsx) */}

      {/* Help & Legal */}
      <div className="border-t pt-4" data-testid="settings-help-section">
        <h3 className="font-semibold mb-2" data-testid="settings-help-heading">
          Help & Legal
        </h3>
        <ul className="list-disc ml-6 text-sm">
          <li>
            <a
              className="underline"
              style={{ color: "#00a7e1" }}
              href="https://zabtranslation.com/terms-and-privacy.html"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="settings-terms-privacy"
            >
              Terms & Privacy
            </a>
          </li>
          <li>
            <a
              className="underline"
              style={{ color: "#00a7e1" }}
              href="mailto:support@zabtranslation.com"
              data-testid="settings-contact-support"
            >
              Contact Support
            </a>
          </li>
        </ul>
      </div>
    </div>
  );
}